import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environment/environment';

interface AppState {
  showMenu: boolean;
  isGaLoaded: boolean;
}

const APP_INITIAL_STATE = {
  showMenu: null,
  isGaLoaded: false,
};

export enum FeatureFlag {
  address_format_v2_enabled = 'address_format_v2_enabled',
}

export interface FeatureFlags {
  [FeatureFlag.address_format_v2_enabled]?: boolean;
}

export interface AppConfig {
  maintenance_mode: boolean;
  feature_flags?: FeatureFlags;
}

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private store = new BehaviorSubject<AppState>(APP_INITIAL_STATE);
  private appConfigSubject: BehaviorSubject<AppConfig> = new BehaviorSubject({} as AppConfig);
  private appConfig$: Observable<AppConfig> = this.appConfigSubject.asObservable()
    .pipe(shareReplay(1));

  public isGaLoaded$ = this.store.asObservable().pipe(
    map(v => v.isGaLoaded)
  );

  constructor(private httpClient: HttpClient) {
  }

  setShowMenu(showMenu: boolean) {
    this.store.next({
      ...this.store.getValue(),
      showMenu
    });
  }

  setIsGaLoaded(isGaLoaded: boolean) {
    this.store.next({
      ...this.store.getValue(),
      isGaLoaded
    });
  }

  loadAppConfig(): Observable<AppConfig> {
    if (environment?.configUrl === '') {
      return throwError('No app configuration set');
    }

    return this.httpClient.get<AppConfig>(environment.configUrl);
  }

  setAppConfig(config: AppConfig): void {
    this.appConfigSubject.next(config);
  }

  isFeatureFlagEnabled$(flagName: FeatureFlag): Observable<boolean> {
    if (flagName === FeatureFlag.address_format_v2_enabled) {
      return of(true);
    }
    return this.appConfig$.pipe(
      map(config => config?.feature_flags?.[flagName]),
      distinctUntilChanged()
    );
  }
}
